import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import 'rxjs/add/operator/switchMap';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { PageArgs } from '../../models/projects/operation.models';
import { Observable } from 'rxjs/Observable';
import { SidebarPermissions } from '../../models/permissions/permission.models';
import { MenuSection } from './menu-sections';
import { AppConfig } from '../../app/app.config';

@Injectable()
export class NavHelperService {

  private initialSetupCompleteSharedObj = new BehaviorSubject<boolean>(false);
  currentInitialSetupComplete: Observable<boolean>;

  private setupConfiguredSharedObj = new BehaviorSubject<boolean>(false);
  currentSetupConfigured: Observable<boolean>;

  private navPageSharedObj;
  currentNavPage: Observable<PageArgs>;

  private navMenuPageSharedObj;
  currentNavMenuPage: Observable<PageArgs>;

  private sidebarCollapsedSharedObj = new BehaviorSubject<boolean>(false);
  currentSidebarCollapsed: Observable<boolean>;

  private canEnterDataSharedObj = new BehaviorSubject<boolean>(false);
  canEnterData: Observable<boolean>;

  private dailyLogStaleSharedObj = new BehaviorSubject<boolean>(false);
  dailyLogStale: Observable<boolean>;

  // check if daily log requires reload, this exists so we don't need to reload the log every time the user returns from the validation
  // errors modal on the daily log (because it's really annoying) - but any time the ngOnInit runs it will reload fully.
  private dailyLogRequiresReloadSharedObj = new BehaviorSubject<boolean>(true);
  dailyLogRequiresReload = this.dailyLogRequiresReloadSharedObj.asObservable();

  // check if leg load log requires reload - i.e after user has added a new recording frequency, as the ngOnInit won't be triggered
  private legLoadLogRequiresReloadSharedObj = new BehaviorSubject<boolean>(true);
  legLoadLogRequiresReload = this.legLoadLogRequiresReloadSharedObj.asObservable();

  // check if weather log requires reload - i.e after user has added a new reading, as the ngOnInit won't be triggered
  private weatherLogRequiresReloadSharedObj = new BehaviorSubject<boolean>(true);
  weatherLogRequiresReload = this.weatherLogRequiresReloadSharedObj.asObservable();

  // check if select operation page requires reload, this exists so we don't need to reload the page every time the user returns
  // from the operation edit screen (because it's really annoying) - but any time the ngOnInit runs it will reload fully.
  private selectOperationRequiresReloadSharedObj = new BehaviorSubject<boolean>(true);
  selectOperationRequiresReload = this.selectOperationRequiresReloadSharedObj.asObservable();

  unsavedChanges: boolean = false;
  changesSaved: boolean;
  triedToSave: boolean;
  invalidComments: boolean;

  warned: boolean = true;
  showWarning: boolean = false;

  // need separate warning variables for sub-pages
  commentWarned: boolean = true;
  showCommentWarning: boolean = false;
  unsavedComment: boolean = false;

  constructor(
    private http: HttpClient,
    private page: any
  ) {
    this.navPageSharedObj = new BehaviorSubject<any>({page});
    this.navMenuPageSharedObj = new BehaviorSubject<any>({page});

    this.currentSetupConfigured = this.setupConfiguredSharedObj.asObservable();
    this.currentNavPage = this.navPageSharedObj.asObservable();
    this.currentNavMenuPage = this.navMenuPageSharedObj.asObservable();
    this.currentInitialSetupComplete = this.initialSetupCompleteSharedObj.asObservable();
    this.canEnterData = this.canEnterDataSharedObj.asObservable();
    this.dailyLogStale = this.dailyLogStaleSharedObj.asObservable();
    this.currentSidebarCollapsed = this.sidebarCollapsedSharedObj.asObservable();
  }

  // used in deciding which menu items are displayed to the user on initial app load
  updateInitialSetupComplete(flag: boolean) {
    this.initialSetupCompleteSharedObj.next(flag);
  }

  // differs from updateInitialSetupComplete in that this is used to determine if the setup has been configured for any given day, whereas
  // updateInitialSetupComplete() only determines if setup for any day at all has been configured.
  updateSetupConfigured(flag: boolean) {
    this.setupConfiguredSharedObj.next(flag);
  }

  updateNavPage(page: PageArgs) {
    this.navPageSharedObj.next(page);
  }

  // Match the main-menu guards, including access through the DPR Sections submenu.
  canOpenMenuSection(section: MenuSection, permissions: SidebarPermissions,
                     isNonJackingVessel: boolean, implementerActive: boolean): boolean {
    return !!section && !!permissions[section.permission]
      && !(section.requiresJacking && isNonJackingVessel)
      && this.initialSetupCompleteSharedObj.getValue()
      && (!section.requiresDaySetup || (this.setupConfiguredSharedObj.getValue() && this.canEnterDataSharedObj.getValue()))
      && (!AppConfig.settings.isImplementerEnvironment || implementerActive);
  }

  navigateFromMenu(page: PageArgs): boolean {
    if (this.warned || this.unsavedChanges === false) {
      this.unsavedChanges = false;
      this.updateNavPage(page);
      return true;
    }

    this.warned = true;
    this.showWarning = true;
    return false;
  }

  updateNavMenuPage(page: PageArgs) {
    this.navMenuPageSharedObj.next(page);
  }

  updateCanEnterData(flag: boolean) {
    this.canEnterDataSharedObj.next(flag);
  }

  updateDailyLogStale(flag: boolean) {
    this.dailyLogStaleSharedObj.next(flag);
  }

  updateDailyLogRequiresReload(flag: boolean) {
    this.dailyLogRequiresReloadSharedObj.next(flag);
  }

  updateLegLoadLogRequiresReload(flag: boolean) {
    this.legLoadLogRequiresReloadSharedObj.next(flag);
  }

  updateWeatherLogRequiresReload(flag: boolean) {
    this.weatherLogRequiresReloadSharedObj.next(flag);
  }

  updateSelectOperationRequiresReload(flag: boolean) {
    this.selectOperationRequiresReloadSharedObj.next(flag);
  }

  toggleSidebarCollapse() {
    switch (this.sidebarCollapsedSharedObj.getValue()) {
      case true:
        this.sidebarCollapsedSharedObj.next(false);
        break;
      case false:
        this.sidebarCollapsedSharedObj.next(true);
        break;
    }
  }

  dataSavedCallback = (dataChanged) => {
    return new Promise((resolve, reject) => {
      this.unsavedChanges = dataChanged;
      resolve(null);
    });
  };
}



// WEBPACK FOOTER //
// ./src/services/nav/nav-helper.service.ts